import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiCopy, FiUpload, FiArrowUp, FiArrowDown, FiX } from 'react-icons/fi';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { apiRequest, getAccessToken, isTokenExpired, refreshAccessToken } from '../utils/auth';
import { useEscapeToClose } from '../utils/useEscapeToClose';
import { hasRichText, parseRuns, stripRichText } from '../utils/richText';
import RichTextField from '../components/RichTextField';
import './PopupsPage.css';

/**
 * Admin-managed app popups (docs/remote-popups-plan.md): a fixed set of native
 * templates whose slots the admin fills, shown by the app when a screen or an
 * in-app event triggers them. The backend validates everything again; the
 * checks here only save a round trip.
 */

type Template = 'fullscreen_hero' | 'feature_list' | 'card' | 'alert';
type TriggerType = 'screen' | 'event';
type LoginState = 'all' | 'guest' | 'loggedIn';
type FrequencyMode = 'once' | 'once_per_session' | 'every_n_days' | 'always';
type Status = 'disabled' | 'scheduled' | 'live' | 'ended';

type ActionType =
  | 'dismiss' | 'continue' | 'login'
  | 'blend' | 'rebuild' | 'upscale' | 'subscription' | 'topup' | 'webSubscription' | 'webTopup'
  | 'extensionList' | 'extension' | 'creator' | 'article' | 'referral' | 'url';

interface LocalizedText {
  en: string;
  [code: string]: string | undefined;
}

interface PopupAction {
  type: ActionType;
  categoryId?: string;
  extensionId?: string;
  userId?: string;
  articleId?: string;
  url?: string;
  /** Admin display only — resolved by the backend, dropped on save. */
  targetName?: string;
}

interface PopupButton {
  label: LocalizedText;
  action: PopupAction;
}

interface PopupBullet {
  iconKey?: string;
  iconUrl?: string;
  text: LocalizedText;
}

interface PopupContent {
  title?: LocalizedText;
  body?: LocalizedText;
  imageKey?: string;
  imageUrl?: string;
  imageAspectRatio?: number;
  showLogo?: boolean;
  bullets?: PopupBullet[];
  primaryButton?: PopupButton;
  secondaryButton?: PopupButton;
  showDoNotShowAgain?: boolean;
}

interface Popup {
  popupId: string;
  name: string;
  enabled: boolean;
  template: Template;
  content: PopupContent;
  trigger: { type: TriggerType; value: string; delayMs: number };
  audience: {
    loginState: LoginState;
    plans: string[];
    platforms: string[];
    languages: string[];
    minAppVersion?: string;
    maxAppVersion?: string;
  };
  schedule: { startDate?: string | null; endDate?: string | null };
  frequency: { mode: FrequencyMode; days?: number; maxImpressions?: number };
  revision: number;
  legacySeenKey?: string;
  /** Imported from this Announcement. */
  sourceAnnouncementId?: string;
  status: Status;
  /** Behaviour of the current revision, from the app's popup events. */
  stats?: PopupStats;
  updatedAt: string;
}

interface PopupStats {
  views: number;
  devices: number;
  users: number;
  primaryClicks: number;
  secondaryClicks: number;
  dismisses: number;
  optOuts: number;
  /** Primary-button presses / views. */
  clickRate: number | null;
}

interface CatalogEntry {
  value: string;
  label: string;
  description?: string;
}

interface Catalog {
  templates: Template[];
  screens: CatalogEntry[];
  events: CatalogEntry[];
  legacySeenKeys: string[];
  upcomingWindowDays: number;
}

interface ArticleOption {
  articleId: string;
  title: string;
}

interface CategoryOption {
  category_id: string;
  label: { en: string; [code: string]: string | undefined };
}

/** The editor's working copy: dates as Date objects, everything else as stored. */
interface Draft {
  popupId?: string;
  name: string;
  enabled: boolean;
  template: Template;
  content: PopupContent;
  trigger: Popup['trigger'];
  audience: Popup['audience'];
  startDate: Date | null;
  endDate: Date | null;
  frequency: Popup['frequency'];
  legacySeenKey: string;
  showAgain: boolean;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const LANGUAGES: { code: string; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'zh-hk', name: '繁中 (HK)' },
  { code: 'zh-tw', name: '繁中 (TW)' },
  { code: 'zh-cn', name: '简中' },
  { code: 'ja', name: '日本語' },
  { code: 'ko', name: '한국어' },
];

const PLANS = ['free', 'basic', 'pro', 'premium'];
const PLATFORMS = [{ id: 'ios', label: 'iOS' }, { id: 'android', label: 'Android' }];

/** The app's highlight colour first (AppTheme text.textHighlight), then accents. */
const TITLE_COLORS = ['#00ffff', '#ffd166', '#ff6b6b', '#7cf29c', '#c792ea'];

const LIMITS = { name: 80, title: 80, body: 400, bulletText: 120, buttonLabel: 24, maxBullets: 6 };

const TEMPLATE_INFO: Record<Template, {
  label: string;
  description: string;
  image: 'required' | 'optional' | 'none';
  bullets: boolean;
  logo: boolean;
  primaryRequired: boolean;
  secondary: boolean;
  bodyRequired: boolean;
  doNotShowAgain: boolean;
}> = {
  fullscreen_hero: {
    label: 'Full-screen hero',
    description: 'Fills the screen: background image, title and text at the bottom, 1–2 buttons. (The app intro.)',
    image: 'required', bullets: false, logo: true, primaryRequired: true, secondary: true, bodyRequired: false, doNotShowAgain: false,
  },
  feature_list: {
    label: 'Feature list',
    description: 'Hero image, title and a list of icon + text rows, 1–2 buttons. (The login intro.)',
    image: 'required', bullets: true, logo: false, primaryRequired: true, secondary: true, bodyRequired: false, doNotShowAgain: false,
  },
  card: {
    label: 'Card',
    description: 'A centred card over a dimmed screen: optional image, title, text, 1–2 buttons.',
    image: 'optional', bullets: false, logo: false, primaryRequired: true, secondary: true, bodyRequired: false, doNotShowAgain: false,
  },
  alert: {
    label: 'Alert',
    description: 'A small text alert (what Announcements were): title, text, optional button and "Don\'t show again".',
    image: 'none', bullets: false, logo: false, primaryRequired: false, secondary: false, bodyRequired: true, doNotShowAgain: true,
  },
};

const ACTION_OPTIONS: { value: ActionType; label: string; group: 'popup' | 'navigate' }[] = [
  { value: 'dismiss', label: 'Close popup', group: 'popup' },
  { value: 'continue', label: 'Close and continue (event popups)', group: 'popup' },
  { value: 'login', label: 'Close and open login', group: 'popup' },
  { value: 'blend', label: 'Camera — Blend', group: 'navigate' },
  { value: 'rebuild', label: 'Camera — Rebuild', group: 'navigate' },
  { value: 'upscale', label: 'Upscale', group: 'navigate' },
  { value: 'subscription', label: 'Subscription page', group: 'navigate' },
  { value: 'topup', label: 'Top-up page', group: 'navigate' },
  { value: 'webSubscription', label: 'Subscription page (web)', group: 'navigate' },
  { value: 'webTopup', label: 'Top-up page (web)', group: 'navigate' },
  { value: 'extensionList', label: 'Lens list (category)', group: 'navigate' },
  { value: 'extension', label: 'Lens detail', group: 'navigate' },
  { value: 'creator', label: 'Creator profile', group: 'navigate' },
  { value: 'article', label: 'Article', group: 'navigate' },
  { value: 'referral', label: 'Invite friends', group: 'navigate' },
  { value: 'url', label: 'Website (phone browser)', group: 'navigate' },
];

const FREQUENCY_OPTIONS: { value: FrequencyMode; label: string }[] = [
  { value: 'once', label: 'Once per device' },
  { value: 'once_per_session', label: 'Once per app session' },
  { value: 'every_n_days', label: 'At most every N days' },
  { value: 'always', label: 'Every time it triggers' },
];

const LOGIN_STATES: { value: LoginState; label: string }[] = [
  { value: 'all', label: 'Everyone' },
  { value: 'guest', label: 'Guests only' },
  { value: 'loggedIn', label: 'Logged-in only' },
];

const STATUS_LABELS: Record<Status, string> = {
  live: 'Live',
  scheduled: 'Scheduled',
  ended: 'Ended',
  disabled: 'Disabled',
};

const emptyText = (): LocalizedText => ({ en: '' });

const newDraft = (): Draft => ({
  name: '',
  enabled: false,
  template: 'card',
  content: {
    title: emptyText(),
    body: emptyText(),
    primaryButton: { label: { en: 'OK' }, action: { type: 'dismiss' } },
  },
  trigger: { type: 'screen', value: 'home', delayMs: 500 },
  audience: { loginState: 'all', plans: [], platforms: [], languages: [] },
  startDate: null,
  endDate: null,
  frequency: { mode: 'once' },
  legacySeenKey: '',
  showAgain: false,
});

const draftFromPopup = (popup: Popup): Draft => ({
  popupId: popup.popupId,
  name: popup.name,
  enabled: popup.enabled,
  template: popup.template,
  content: {
    ...popup.content,
    title: popup.content.title || emptyText(),
    body: popup.content.body || emptyText(),
  },
  trigger: popup.trigger,
  audience: { ...popup.audience, languages: popup.audience.languages || [] },
  startDate: popup.schedule?.startDate ? new Date(popup.schedule.startDate) : null,
  endDate: popup.schedule?.endDate ? new Date(popup.schedule.endDate) : null,
  frequency: popup.frequency,
  legacySeenKey: popup.legacySeenKey || '',
  showAgain: false,
});

/** Trims every translation and drops empty ones; undefined when nothing is left. */
const cleanText = (text?: LocalizedText): LocalizedText | undefined => {
  if (!text) return undefined;
  const out: Record<string, string> = {};
  for (const { code } of LANGUAGES) {
    const value = (text[code] || '').trim();
    if (value) out[code] = value;
  }
  return Object.keys(out).length ? (out as LocalizedText) : undefined;
};

const cleanAction = ({ targetName: _ignored, ...action }: PopupAction): PopupAction => action;

const cleanButton = (button?: PopupButton): PopupButton | undefined =>
  button ? { label: cleanText(button.label) || emptyText(), action: cleanAction(button.action) } : undefined;

/** The save payload: only the slots the template uses, texts cleaned. */
const payloadFromDraft = (draft: Draft) => {
  const info = TEMPLATE_INFO[draft.template];
  const { content } = draft;
  return {
    name: draft.name.trim(),
    enabled: draft.enabled,
    template: draft.template,
    content: {
      title: cleanText(content.title),
      body: cleanText(content.body),
      ...(info.image !== 'none' && content.imageKey && { imageKey: content.imageKey, imageAspectRatio: content.imageAspectRatio }),
      ...(info.logo && { showLogo: !!content.showLogo }),
      ...(info.bullets && {
        bullets: (content.bullets || []).map((b) => ({ ...(b.iconKey && { iconKey: b.iconKey }), text: cleanText(b.text) || emptyText() })),
      }),
      ...(content.primaryButton && { primaryButton: cleanButton(content.primaryButton) }),
      ...(info.secondary && content.secondaryButton && { secondaryButton: cleanButton(content.secondaryButton) }),
      ...(info.doNotShowAgain && { showDoNotShowAgain: !!content.showDoNotShowAgain }),
    },
    trigger: draft.trigger,
    audience: {
      ...draft.audience,
      minAppVersion: draft.audience.minAppVersion?.trim() || undefined,
      maxAppVersion: draft.audience.maxAppVersion?.trim() || undefined,
    },
    schedule: {
      startDate: draft.startDate ? draft.startDate.toISOString() : null,
      endDate: draft.endDate ? draft.endDate.toISOString() : null,
    },
    frequency: draft.frequency,
    legacySeenKey: draft.legacySeenKey || undefined,
    showAgain: draft.showAgain,
  };
};

const actionError = (action: PopupAction, where: string): string | null => {
  if (action.type === 'extensionList' && !action.categoryId) return `${where}: pick a category`;
  if (action.type === 'extension' && !action.extensionId?.trim()) return `${where}: enter the lens ID`;
  if (action.type === 'creator' && !action.userId?.trim()) return `${where}: enter the creator's user ID`;
  if (action.type === 'article' && !action.articleId) return `${where}: pick an article`;
  if (action.type === 'url') {
    try {
      const { protocol } = new URL((action.url || '').trim());
      if (protocol !== 'http:' && protocol !== 'https:') return `${where}: the website must start with http:// or https://`;
    } catch {
      return `${where}: enter a full website address (https://…)`;
    }
  }
  return null;
};

const buttonError = (button: PopupButton | undefined, where: string, required: boolean): string | null => {
  if (!button) return required ? `${where} is required` : null;
  if (!(button.label.en || '').trim()) return `${where}: English label is required`;
  return actionError(button.action, where);
};

/** Mirrors the backend's rules so the admin sees the error before saving. */
const draftError = (draft: Draft): string | null => {
  const info = TEMPLATE_INFO[draft.template];
  const { content } = draft;
  if (!draft.name.trim()) return 'Name is required';
  if (!(content.title?.en || '').trim()) return 'Title: English is required (it is the fallback for other languages)';
  if (info.bodyRequired && !(content.body?.en || '').trim()) return 'Text: English is required for an alert';
  const translatedOnly = (text?: LocalizedText) =>
    !!text && !(text.en || '').trim() && LANGUAGES.some(({ code }) => (text[code] || '').trim());
  if (translatedOnly(content.body)) return 'Text: English is required when translations are set';
  if (info.image === 'required' && !content.imageKey) return 'Upload an image — this template needs one';
  if (info.bullets) {
    const bullets = content.bullets || [];
    if (!bullets.length) return 'Add at least one feature row';
    const missing = bullets.findIndex((b) => !(b.text.en || '').trim());
    if (missing >= 0) return `Feature row ${missing + 1}: English text is required`;
  }
  const primary = buttonError(content.primaryButton, 'Primary button', info.primaryRequired);
  if (primary) return primary;
  if (info.secondary && content.secondaryButton) {
    const secondary = buttonError(content.secondaryButton, 'Secondary button', true);
    if (secondary) return secondary;
  }
  if (!draft.trigger.value) return 'Pick what triggers the popup';
  if (draft.startDate && draft.endDate && draft.endDate <= draft.startDate) return 'The end date must be after the start date';
  if (draft.frequency.mode === 'every_n_days' && !(Number(draft.frequency.days) >= 1)) return 'Enter how many days to wait between showings';
  return null;
};

/** Events the app waits on before going on with its own flow (see Backend config/popups.ts). */
const GATE_EVENTS = ['guest_fraw_press', 'credits_low'];
const isGateTrigger = (trigger: Popup['trigger']) => trigger.type === 'event' && GATE_EVENTS.includes(trigger.value);

const formatDate = (value?: string | Date | null) =>
  value
    ? new Date(value).toLocaleString([], { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })
    : '—';

const formatUtc = (value: Date | null) => (value ? `${value.toISOString().slice(0, 16).replace('T', ' ')} UTC` : '');

const frequencyLabel = (frequency: Popup['frequency']) => {
  switch (frequency.mode) {
    case 'once': return 'Once';
    case 'once_per_session': return 'Each session';
    case 'every_n_days': return `Every ${frequency.days} day${frequency.days === 1 ? '' : 's'}`;
    case 'always': return 'Every time';
  }
};

/**
 * Views, reach and button use for the current revision. Bumping the revision
 * ("Show again") starts these from zero, since it is a new campaign.
 */
function PopupStatsCell({ stats, revision }: { stats?: PopupStats; revision: number }) {
  if (!stats || stats.views === 0) return <span className="pp-muted">No views yet</span>;
  const percent = stats.clickRate == null ? '—' : `${Math.round(stats.clickRate * 1000) / 10}%`;
  const title = [
    `Revision ${revision}`,
    `${stats.views} views on ${stats.devices} device(s), ${stats.users} signed-in user(s)`,
    `Primary button: ${stats.primaryClicks} · Secondary button: ${stats.secondaryClicks}`,
    `Closed without a button: ${stats.dismisses}`,
    ...(stats.optOuts ? [`"Don't show again": ${stats.optOuts}`] : []),
  ].join('\n');
  return (
    <div className="pp-stats" title={title}>
      <div><strong>{stats.views.toLocaleString()}</strong> views · {stats.devices.toLocaleString()} devices</div>
      <div className="pp-muted">
        {percent} primary · {stats.secondaryClicks} secondary · {stats.dismisses} closed
      </div>
    </div>
  );
}

/** Renders rich-text markup the way the app draws it. */
const RichText = ({ text }: { text: string }) => (
  <>
    {hasRichText(text)
      ? parseRuns(text).map((run, i) => (
          <span
            key={i}
            style={{
              ...(run.color && { color: run.color }),
              ...(run.bold && { fontWeight: 900 }),
              ...(run.italic && { fontStyle: 'italic' as const }),
              ...(run.underline && { textDecoration: 'underline' }),
            }}
          >
            {run.text}
          </span>
        ))
      : text}
  </>
);

const pick = (text: LocalizedText | undefined, language: string) => (text?.[language] || '').trim() || (text?.en || '').trim();

/** Approximate phone preview of a draft in one language — not pixel-exact. */
function PopupPreview({ draft, language }: { draft: Draft; language: string }) {
  const { content, template } = draft;
  const title = pick(content.title, language);
  const body = pick(content.body, language);
  const buttons = [content.primaryButton, TEMPLATE_INFO[template].secondary ? content.secondaryButton : undefined]
    .filter((b): b is PopupButton => !!b);
  const buttonRow = (
    <div className="pp-preview-buttons">
      {buttons.map((button, i) => (
        <span key={i} className={i === 0 ? 'pp-preview-btn primary' : 'pp-preview-btn secondary'}>
          {pick(button.label, language) || 'Button'}
        </span>
      ))}
    </div>
  );

  return (
    <div className="pp-phone">
      {template === 'fullscreen_hero' && (
        <div className="pp-hero" style={content.imageUrl ? { backgroundImage: `url(${content.imageUrl})` } : undefined}>
          {!content.imageUrl && <div className="pp-preview-placeholder">Background image</div>}
          <div className="pp-hero-text">
            {content.showLogo && <div className="pp-preview-logo">FRAW</div>}
            <div className="pp-preview-title large"><RichText text={title || 'Title'} /></div>
            {body && <div className="pp-preview-body"><RichText text={body} /></div>}
            {buttonRow}
          </div>
        </div>
      )}

      {template === 'feature_list' && (
        <div className="pp-feature">
          {content.imageUrl
            ? <img className="pp-feature-image" src={content.imageUrl} alt="" />
            : <div className="pp-preview-placeholder tall">Hero image</div>}
          <div className="pp-feature-text">
            <div className="pp-preview-title"><RichText text={title || 'Title'} /></div>
            {body && <div className="pp-preview-body"><RichText text={body} /></div>}
            <div className="pp-bullets">
              {(content.bullets || []).map((bullet, i) => (
                <div key={i} className="pp-bullet">
                  {bullet.iconUrl ? <img src={bullet.iconUrl} alt="" /> : <span className="pp-bullet-dot" />}
                  <span>{pick(bullet.text, language) || 'Feature'}</span>
                </div>
              ))}
            </div>
            {buttonRow}
          </div>
        </div>
      )}

      {template === 'card' && (
        <div className="pp-dim">
          <div className="pp-card">
            {content.imageUrl && <img className="pp-card-image" src={content.imageUrl} alt="" />}
            <div className="pp-card-text">
              <div className="pp-preview-title"><RichText text={title || 'Title'} /></div>
              {body && <div className="pp-preview-body"><RichText text={body} /></div>}
              {buttonRow}
            </div>
          </div>
        </div>
      )}

      {template === 'alert' && (
        <div className="pp-dim">
          <div className="pp-alert">
            <div className="pp-preview-title small"><RichText text={title || 'Title'} /></div>
            <div className="pp-preview-body"><RichText text={body || 'Text'} /></div>
            {content.showDoNotShowAgain && <label className="pp-preview-check"><input type="checkbox" readOnly /> Don't show again</label>}
            {buttons.length ? buttonRow : <div className="pp-preview-buttons"><span className="pp-preview-btn primary">OK</span></div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PopupsPage() {
  const [popups, setPopups] = useState<Popup[]>([]);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [articles, setArticles] = useState<ArticleOption[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<Status | 'all'>('all');

  const [draft, setDraft] = useState<Draft | null>(null);
  const [language, setLanguage] = useState('en');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      // apiRequest flattens object payloads ({ success, popups, catalog }); array payloads keep `data`
      const [popupRes, articlesRes, categoriesRes] = await Promise.all([
        apiRequest<{ success: boolean; popups: Popup[]; catalog: Catalog }>('/api/admin/popups'),
        apiRequest<{ success: boolean; data: any[] }>('/api/admin/articles').catch(() => ({ success: false, data: [] })),
        apiRequest<{ success: boolean; data: CategoryOption[] }>('/api/admin/categories?page=1&limit=100').catch(() => ({ success: false, data: [] })),
      ]);
      setPopups(popupRes.popups || []);
      setCatalog(popupRes.catalog);
      setArticles((articlesRes.data || []).map((article) => {
        const cover = article.sections?.[0];
        return {
          articleId: article.articleId,
          title: (cover?.type === 'articleCoverImage' && cover.title) || article.title || article.articleId,
        };
      }));
      setCategories(categoriesRes.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load popups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const closeEditor = useCallback(() => {
    if (saving || uploading) return;
    setDraft(null);
    setFormError(null);
  }, [saving, uploading]);

  useEscapeToClose(!!draft, closeEditor);

  const openCreate = () => {
    setDraft(newDraft());
    setLanguage('en');
    setFormError(null);
  };

  const openEdit = (popup: Popup) => {
    setDraft(draftFromPopup(popup));
    setLanguage('en');
    setFormError(null);
  };

  const openDuplicate = (popup: Popup) => {
    const copy = draftFromPopup(popup);
    setDraft({ ...copy, popupId: undefined, name: `${popup.name} (copy)`.slice(0, LIMITS.name), enabled: false, legacySeenKey: '' });
    setLanguage('en');
    setFormError(null);
  };

  const update = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const updateContent = (patch: Partial<PopupContent>) =>
    setDraft((d) => (d ? { ...d, content: { ...d.content, ...patch } } : d));

  const setText = (field: 'title' | 'body', value: string) =>
    setDraft((d) => (d ? { ...d, content: { ...d.content, [field]: { ...(d.content[field] || emptyText()), [language]: value } } } : d));

  const uploadImage = async (file: File): Promise<{ imageKey: string; imageUrl?: string; aspectRatio?: number } | null> => {
    try {
      setUploading(true);
      if (isTokenExpired()) await refreshAccessToken();
      const formData = new FormData();
      formData.append('image', file);
      const response = await fetch(`${API_URL}/api/admin/popups/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAccessToken()}` },
        body: formData,
      });
      const json = await response.json();
      if (!json.success) {
        alert('Failed to upload image: ' + (json.error?.message || 'Unknown error'));
        return null;
      }
      return json.data;
    } catch (err) {
      alert('Failed to upload image');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleMainImage = async (file?: File) => {
    if (!file) return;
    const uploaded = await uploadImage(file);
    if (uploaded) updateContent({ imageKey: uploaded.imageKey, imageUrl: uploaded.imageUrl, imageAspectRatio: uploaded.aspectRatio });
  };

  const bullets = draft?.content.bullets || [];
  const setBullets = (next: PopupBullet[]) => updateContent({ bullets: next });
  const handleBulletIcon = async (index: number, file?: File) => {
    if (!file) return;
    const uploaded = await uploadImage(file);
    if (!uploaded) return;
    setDraft((d) => {
      if (!d) return d;
      const next = [...(d.content.bullets || [])];
      next[index] = { ...next[index], iconKey: uploaded.imageKey, iconUrl: uploaded.imageUrl };
      return { ...d, content: { ...d.content, bullets: next } };
    });
  };

  const setButton = (slot: 'primaryButton' | 'secondaryButton', button: PopupButton | undefined) => updateContent({ [slot]: button });

  const handleSave = async () => {
    if (!draft) return;
    const problem = draftError(draft);
    if (problem) {
      setFormError(problem);
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const body = JSON.stringify(payloadFromDraft(draft));
      if (draft.popupId) {
        await apiRequest(`/api/admin/popups/${draft.popupId}`, { method: 'PUT', body });
      } else {
        await apiRequest('/api/admin/popups', { method: 'POST', body });
      }
      setDraft(null);
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save popup');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (popup: Popup) => {
    try {
      const payload = payloadFromDraft({ ...draftFromPopup(popup), enabled: !popup.enabled });
      await apiRequest(`/api/admin/popups/${popup.popupId}`, { method: 'PUT', body: JSON.stringify(payload) });
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update popup');
    }
  };

  const [importing, setImporting] = useState(false);

  /** Phase 4: each Announcement that hasn't ended becomes an alert popup (safe to repeat). */
  const handleImportAnnouncements = async () => {
    if (!window.confirm(
      'Create an alert popup for each announcement that hasn\'t ended?\n\n'
      + 'Each one shows at app launch with "Don\'t show again", between the announcement\'s dates. '
      + 'Announcements already imported are skipped. The announcements themselves stay as they are: '
      + 'older app versions still read them.',
    )) return;
    setImporting(true);
    try {
      const result = await apiRequest<{
        success: boolean;
        imported: { title: string; truncated: boolean }[];
        skipped: { title: string; reason: string }[];
      }>('/api/admin/popups/import-announcements', { method: 'POST' });
      const lines = [
        `Imported ${result.imported.length} announcement(s).`,
        ...result.imported.filter((i) => i.truncated).map((i) => `• "${i.title}" was shortened to fit an alert — check its text.`),
        ...result.skipped.filter((s) => s.reason !== 'Already imported' && s.reason !== 'Already ended')
          .map((s) => `• "${s.title}" not imported: ${s.reason}`),
      ];
      const repeats = result.skipped.filter((s) => s.reason === 'Already imported').length;
      const ended = result.skipped.filter((s) => s.reason === 'Already ended').length;
      if (repeats) lines.push(`${repeats} already imported before.`);
      if (ended) lines.push(`${ended} already ended, skipped.`);
      alert(lines.join('\n'));
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to import announcements');
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (popup: Popup) => {
    if (!window.confirm(`Delete "${popup.name}"? This cannot be undone.`)) return;
    try {
      await apiRequest(`/api/admin/popups/${popup.popupId}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      alert('Failed to delete popup');
    }
  };

  const triggerLabel = useCallback((trigger: Popup['trigger']) => {
    const list = trigger.type === 'screen' ? catalog?.screens : catalog?.events;
    const label = list?.find((entry) => entry.value === trigger.value)?.label || trigger.value;
    return trigger.type === 'screen' ? `Screen: ${label}` : `Event: ${label}`;
  }, [catalog]);

  const visible = useMemo(
    () => (statusFilter === 'all' ? popups : popups.filter((p) => p.status === statusFilter)),
    [popups, statusFilter]
  );

  if (loading) return <div className="page pp-page"><div className="pp-empty">Loading popups…</div></div>;
  if (error) return <div className="page pp-page"><div className="pp-empty pp-error-text">{error}</div></div>;

  const info = draft ? TEMPLATE_INFO[draft.template] : null;
  const triggerOptions = draft?.trigger.type === 'event' ? catalog?.events || [] : catalog?.screens || [];
  const selectedEvent = draft?.trigger.type === 'event' ? catalog?.events.find((e) => e.value === draft.trigger.value) : undefined;

  const renderButtonEditor = (slot: 'primaryButton' | 'secondaryButton', title: string) => {
    if (!draft) return null;
    const button = draft.content[slot];
    const optional = slot === 'secondaryButton' || !info?.primaryRequired;
    if (!button) {
      return (
        <div className="pp-subsection">
          <div className="pp-subsection-head">
            <span>{title}</span>
            <button type="button" className="pp-link-btn" onClick={() => setButton(slot, { label: emptyText(), action: { type: 'dismiss' } })}>
              <FiPlus /> Add
            </button>
          </div>
        </div>
      );
    }
    const setAction = (patch: Partial<PopupAction>) => setButton(slot, { ...button, action: { ...button.action, ...patch } });
    return (
      <div className="pp-subsection">
        <div className="pp-subsection-head">
          <span>{title}</span>
          {optional && (
            <button type="button" className="pp-link-btn danger" onClick={() => setButton(slot, undefined)}>
              <FiX /> Remove
            </button>
          )}
        </div>
        <div className="pp-row">
          <label className="pp-field">
            <span>Label ({language}){language === 'en' ? ' *' : ''}</span>
            <input
              value={button.label[language] || ''}
              maxLength={LIMITS.buttonLabel}
              placeholder={language === 'en' ? '' : button.label.en}
              onChange={(e) => setButton(slot, { ...button, label: { ...button.label, [language]: e.target.value } })}
            />
          </label>
          <label className="pp-field">
            <span>When tapped</span>
            <select value={button.action.type} onChange={(e) => setButton(slot, { ...button, action: { type: e.target.value as ActionType } })}>
              <optgroup label="Close the popup">
                {ACTION_OPTIONS.filter((o) => o.group === 'popup').map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </optgroup>
              <optgroup label="Close and go to">
                {ACTION_OPTIONS.filter((o) => o.group === 'navigate').map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </optgroup>
            </select>
          </label>
        </div>
        {button.action.type === 'extensionList' && (
          <label className="pp-field">
            <span>Category</span>
            <select value={button.action.categoryId || ''} onChange={(e) => setAction({ categoryId: e.target.value })}>
              <option value="">Select a category…</option>
              {categories.map((c) => <option key={c.category_id} value={c.category_id}>{c.label?.en || c.category_id}</option>)}
            </select>
          </label>
        )}
        {button.action.type === 'article' && (
          <label className="pp-field">
            <span>Article</span>
            <select value={button.action.articleId || ''} onChange={(e) => setAction({ articleId: e.target.value })}>
              <option value="">Select an article…</option>
              {articles.map((a) => <option key={a.articleId} value={a.articleId}>{a.title}</option>)}
            </select>
          </label>
        )}
        {button.action.type === 'extension' && (
          <label className="pp-field">
            <span>Lens ID {button.action.targetName && <em>— {button.action.targetName}</em>}</span>
            <input value={button.action.extensionId || ''} onChange={(e) => setAction({ extensionId: e.target.value.trim(), targetName: undefined })} />
          </label>
        )}
        {button.action.type === 'creator' && (
          <label className="pp-field">
            <span>Creator user ID {button.action.targetName && <em>— {button.action.targetName}</em>}</span>
            <input value={button.action.userId || ''} onChange={(e) => setAction({ userId: e.target.value.trim(), targetName: undefined })} />
          </label>
        )}
        {button.action.type === 'url' && (
          <label className="pp-field">
            <span>Website</span>
            <input value={button.action.url || ''} placeholder="https://…" onChange={(e) => setAction({ url: e.target.value })} />
          </label>
        )}
        {button.action.type === 'continue' && !isGateTrigger(draft.trigger) && (
          <small className="pp-hint">"Continue" only means something for the gate events (Guest presses FRAW, Credits low); here it just closes.</small>
        )}
        {isGateTrigger(draft.trigger) && button.action.type !== 'continue' && button.action.type !== 'login' && (
          <small className="pp-hint">At this gate, this button stops what the user was doing. Use "Close and continue" to let it go on.</small>
        )}
      </div>
    );
  };

  return (
    <div className="page pp-page">
      <div className="pp-header">
        <div>
          <h1>Popups</h1>
          <p className="pp-subtitle">Popups the app shows when a screen opens or an in-app event happens.</p>
        </div>
        <div className="pp-header-actions">
          <button className="pp-secondary-btn" onClick={handleImportAnnouncements} disabled={importing}>
            {importing ? 'Importing…' : 'Import announcements'}
          </button>
          <button className="pp-primary-btn" onClick={openCreate}><FiPlus /> Create popup</button>
        </div>
      </div>

      <div className="pp-filters">
        {(['all', 'live', 'scheduled', 'ended', 'disabled'] as const).map((status) => (
          <button
            key={status}
            className={`pp-chip ${statusFilter === status ? 'active' : ''}`}
            onClick={() => setStatusFilter(status)}
          >
            {status === 'all' ? 'All' : STATUS_LABELS[status]}
            <span className="pp-chip-count">{status === 'all' ? popups.length : popups.filter((p) => p.status === status).length}</span>
          </button>
        ))}
      </div>

      <table className="pp-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Template</th>
            <th>Trigger</th>
            <th>Schedule</th>
            <th>Frequency</th>
            <th>Performance</th>
            <th>Status</th>
            <th>Enabled</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {visible.length === 0 ? (
            <tr><td colSpan={9} className="pp-empty">{popups.length ? 'No popups with this status.' : 'No popups yet.'}</td></tr>
          ) : visible.map((popup) => (
            <tr key={popup.popupId}>
              <td data-label="Name">
                <div className="pp-name">
                  {popup.name}
                  {popup.sourceAnnouncementId && <span className="pp-tag">From announcement</span>}
                </div>
                <div className="pp-muted">{stripRichText(popup.content.title?.en || '')}</div>
              </td>
              <td data-label="Template">{TEMPLATE_INFO[popup.template]?.label || popup.template}</td>
              <td data-label="Trigger">{triggerLabel(popup.trigger)}</td>
              <td data-label="Schedule">
                <div>{formatDate(popup.schedule?.startDate)}</div>
                <div className="pp-muted">→ {formatDate(popup.schedule?.endDate)}</div>
              </td>
              <td data-label="Frequency">{frequencyLabel(popup.frequency)}</td>
              <td data-label="Performance"><PopupStatsCell stats={popup.stats} revision={popup.revision} /></td>
              <td data-label="Status"><span className={`pp-status ${popup.status}`}>{STATUS_LABELS[popup.status]}</span></td>
              <td data-label="Enabled">
                <label className="pp-switch">
                  <input type="checkbox" checked={popup.enabled} onChange={() => toggleEnabled(popup)} />
                  <span />
                </label>
              </td>
              <td data-label="Actions" className="pp-actions">
                <button className="pp-icon-btn" title="Edit" onClick={() => openEdit(popup)}><FiEdit2 /></button>
                <button className="pp-icon-btn" title="Duplicate" onClick={() => openDuplicate(popup)}><FiCopy /></button>
                <button className="pp-icon-btn danger" title="Delete" onClick={() => handleDelete(popup)}><FiTrash2 /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {draft && info && (
        <div className="pp-overlay" onClick={closeEditor}>
          <div className="pp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pp-modal-head">
              <h2>{draft.popupId ? 'Edit popup' : 'Create popup'}</h2>
              <button className="pp-icon-btn" onClick={closeEditor} aria-label="Close"><FiX /></button>
            </div>

            <div className="pp-modal-body">
              <div className="pp-form">
                <section className="pp-section">
                  <h3>Basics</h3>
                  <label className="pp-field">
                    <span>Name (admin only) *</span>
                    <input value={draft.name} maxLength={LIMITS.name} onChange={(e) => update({ name: e.target.value })} placeholder="e.g. Christmas 2026" />
                  </label>
                  <label className="pp-check">
                    <input type="checkbox" checked={draft.enabled} onChange={(e) => update({ enabled: e.target.checked })} />
                    Enabled
                  </label>
                  <div className="pp-templates">
                    {(catalog?.templates || (Object.keys(TEMPLATE_INFO) as Template[])).map((template) => (
                      <button
                        type="button"
                        key={template}
                        className={`pp-template ${draft.template === template ? 'active' : ''}`}
                        onClick={() => update({ template })}
                      >
                        <strong>{TEMPLATE_INFO[template].label}</strong>
                        <span>{TEMPLATE_INFO[template].description}</span>
                      </button>
                    ))}
                  </div>
                </section>

                <section className="pp-section">
                  <h3>Content</h3>
                  <div className="pp-lang-tabs">
                    {LANGUAGES.map(({ code, name }) => (
                      <button
                        type="button"
                        key={code}
                        className={`pp-lang ${language === code ? 'active' : ''}`}
                        onClick={() => setLanguage(code)}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                  <small className="pp-hint">
                    English is required; a language left empty shows the English text. Select text to bold or colour it — colour is how a word is highlighted.
                  </small>

                  <div className="pp-field">
                    <span>Title ({language}){language === 'en' ? ' *' : ''}</span>
                    <RichTextField
                      key={`title-${language}`}
                      value={draft.content.title?.[language] || ''}
                      onChange={(markup) => setText('title', markup)}
                      placeholder={language === 'en' ? 'Title' : stripRichText(draft.content.title?.en || '')}
                      colors={TITLE_COLORS}
                      ariaLabel="Title"
                    />
                  </div>
                  <label className="pp-field">
                    <span>Text ({language}){info.bodyRequired && language === 'en' ? ' *' : ''}</span>
                    <textarea
                      rows={3}
                      maxLength={LIMITS.body}
                      value={draft.content.body?.[language] || ''}
                      placeholder={language === 'en' ? '' : draft.content.body?.en}
                      onChange={(e) => setText('body', e.target.value)}
                    />
                  </label>

                  {info.image !== 'none' && (
                    <div className="pp-field">
                      <span>{draft.template === 'fullscreen_hero' ? 'Background image' : 'Image'}{info.image === 'required' ? ' *' : ' (optional)'}</span>
                      <div className="pp-upload-row">
                        {draft.content.imageUrl && <img className="pp-thumb" src={draft.content.imageUrl} alt="" />}
                        <label className="pp-upload-btn">
                          <FiUpload /> {draft.content.imageKey ? 'Replace' : 'Upload'}
                          <input type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => handleMainImage(e.target.files?.[0])} />
                        </label>
                        {draft.content.imageKey && info.image === 'optional' && (
                          <button type="button" className="pp-link-btn danger" onClick={() => updateContent({ imageKey: undefined, imageUrl: undefined, imageAspectRatio: undefined })}>
                            Remove
                          </button>
                        )}
                        {uploading && <small className="pp-hint">Uploading…</small>}
                      </div>
                      <small className="pp-hint">Upload PNG to keep transparency.</small>
                    </div>
                  )}

                  {info.logo && (
                    <label className="pp-check">
                      <input type="checkbox" checked={!!draft.content.showLogo} onChange={(e) => updateContent({ showLogo: e.target.checked })} />
                      Show the FRAW logo above the title
                    </label>
                  )}

                  {info.bullets && (
                    <div className="pp-subsection">
                      <div className="pp-subsection-head">
                        <span>Feature rows *</span>
                        {bullets.length < LIMITS.maxBullets && (
                          <button type="button" className="pp-link-btn" onClick={() => setBullets([...bullets, { text: emptyText() }])}>
                            <FiPlus /> Add row
                          </button>
                        )}
                      </div>
                      {bullets.map((bullet, index) => (
                        <div key={index} className="pp-bullet-row">
                          <label className="pp-icon-upload" title="Upload icon">
                            {bullet.iconUrl ? <img src={bullet.iconUrl} alt="" /> : <FiUpload />}
                            <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden onChange={(e) => handleBulletIcon(index, e.target.files?.[0])} />
                          </label>
                          <input
                            value={bullet.text[language] || ''}
                            maxLength={LIMITS.bulletText}
                            placeholder={language === 'en' ? `Row ${index + 1}` : bullet.text.en}
                            onChange={(e) => {
                              const next = [...bullets];
                              next[index] = { ...bullet, text: { ...bullet.text, [language]: e.target.value } };
                              setBullets(next);
                            }}
                          />
                          <button type="button" className="pp-icon-btn" disabled={index === 0} onClick={() => {
                            const next = [...bullets];
                            [next[index - 1], next[index]] = [next[index], next[index - 1]];
                            setBullets(next);
                          }}><FiArrowUp /></button>
                          <button type="button" className="pp-icon-btn" disabled={index === bullets.length - 1} onClick={() => {
                            const next = [...bullets];
                            [next[index + 1], next[index]] = [next[index], next[index + 1]];
                            setBullets(next);
                          }}><FiArrowDown /></button>
                          <button type="button" className="pp-icon-btn danger" onClick={() => setBullets(bullets.filter((_, i) => i !== index))}><FiTrash2 /></button>
                        </div>
                      ))}
                    </div>
                  )}

                  {renderButtonEditor('primaryButton', info.primaryRequired ? 'Primary button *' : 'Button (optional — the app shows OK)')}
                  {info.secondary && draft.content.primaryButton && renderButtonEditor('secondaryButton', 'Secondary button (optional)')}

                  {info.doNotShowAgain && (
                    <label className="pp-check">
                      <input type="checkbox" checked={!!draft.content.showDoNotShowAgain} onChange={(e) => updateContent({ showDoNotShowAgain: e.target.checked })} />
                      Offer a "Don't show again" checkbox
                    </label>
                  )}
                </section>

                <section className="pp-section">
                  <h3>Trigger</h3>
                  <div className="pp-segments">
                    {(['screen', 'event'] as const).map((type) => (
                      <button
                        type="button"
                        key={type}
                        className={`pp-segment ${draft.trigger.type === type ? 'active' : ''}`}
                        onClick={() => update({
                          trigger: {
                            ...draft.trigger,
                            type,
                            value: (type === 'screen' ? catalog?.screens[0]?.value : catalog?.events[0]?.value) || '',
                          },
                        })}
                      >
                        {type === 'screen' ? 'When a screen opens' : 'When something happens'}
                      </button>
                    ))}
                  </div>
                  <div className="pp-row">
                    <label className="pp-field">
                      <span>{draft.trigger.type === 'screen' ? 'Screen' : 'Event'}</span>
                      <select value={draft.trigger.value} onChange={(e) => update({ trigger: { ...draft.trigger, value: e.target.value } })}>
                        {triggerOptions.map((entry) => <option key={entry.value} value={entry.value}>{entry.label}</option>)}
                      </select>
                    </label>
                    <label className="pp-field narrow">
                      <span>Delay (ms)</span>
                      <input
                        type="number"
                        min={0}
                        max={10000}
                        step={100}
                        value={draft.trigger.delayMs}
                        onChange={(e) => update({ trigger: { ...draft.trigger, delayMs: Math.max(0, Number(e.target.value) || 0) } })}
                      />
                    </label>
                  </div>
                  {selectedEvent?.description && <small className="pp-hint">{selectedEvent.description}</small>}
                  {draft.trigger.type === 'screen' && (
                    <small className="pp-hint">The camera, login and subscription screens never show popups.</small>
                  )}
                </section>

                <section className="pp-section">
                  <h3>Audience</h3>
                  <div className="pp-segments">
                    {LOGIN_STATES.map((s) => (
                      <button
                        type="button"
                        key={s.value}
                        className={`pp-segment ${draft.audience.loginState === s.value ? 'active' : ''}`}
                        onClick={() => update({ audience: { ...draft.audience, loginState: s.value } })}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                  {(
                    [
                      { key: 'plans', label: 'Plans', options: PLANS.map((p) => ({ id: p, label: p })) },
                      { key: 'platforms', label: 'Platforms', options: PLATFORMS },
                      { key: 'languages', label: 'App languages', options: LANGUAGES.map((l) => ({ id: l.code, label: l.name })) },
                    ] as const
                  ).map(({ key, label, options }) => (
                    <div key={key} className="pp-field">
                      <span>{label} <em className="pp-muted">(none ticked = all)</em></span>
                      <div className="pp-checks">
                        {options.map((option) => (
                          <label key={option.id} className="pp-check inline">
                            <input
                              type="checkbox"
                              checked={draft.audience[key].includes(option.id)}
                              onChange={(e) => update({
                                audience: {
                                  ...draft.audience,
                                  [key]: e.target.checked
                                    ? [...draft.audience[key], option.id]
                                    : draft.audience[key].filter((v) => v !== option.id),
                                },
                              })}
                            />
                            {option.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="pp-row">
                    <label className="pp-field">
                      <span>Min app version</span>
                      <input value={draft.audience.minAppVersion || ''} placeholder="e.g. 1.6.0" onChange={(e) => update({ audience: { ...draft.audience, minAppVersion: e.target.value } })} />
                    </label>
                    <label className="pp-field">
                      <span>Max app version</span>
                      <input value={draft.audience.maxAppVersion || ''} placeholder="optional" onChange={(e) => update({ audience: { ...draft.audience, maxAppVersion: e.target.value } })} />
                    </label>
                  </div>
                </section>

                <section className="pp-section">
                  <h3>Schedule</h3>
                  <small className="pp-hint">
                    Times are in your local time zone. Leave empty for no limit. The app downloads a popup up to {catalog?.upcomingWindowDays ?? 7} days before it starts, so its images are ready on the day.
                  </small>
                  <div className="pp-row">
                    <div className="pp-field">
                      <span>Start</span>
                      <DatePicker
                        selected={draft.startDate}
                        onChange={(date: Date | null) => update({ startDate: date })}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="yyyy-MM-dd HH:mm"
                        placeholderText="Immediately"
                        className="pp-date"
                        isClearable
                      />
                      <small className="pp-muted">{formatUtc(draft.startDate)}</small>
                    </div>
                    <div className="pp-field">
                      <span>End</span>
                      <DatePicker
                        selected={draft.endDate}
                        onChange={(date: Date | null) => update({ endDate: date })}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="yyyy-MM-dd HH:mm"
                        placeholderText="No end"
                        minDate={draft.startDate || undefined}
                        className="pp-date"
                        isClearable
                      />
                      <small className="pp-muted">{formatUtc(draft.endDate)}</small>
                    </div>
                  </div>
                </section>

                <section className="pp-section">
                  <h3>Frequency</h3>
                  <div className="pp-row">
                    <label className="pp-field">
                      <span>Show</span>
                      <select
                        value={draft.frequency.mode}
                        onChange={(e) => update({ frequency: { ...draft.frequency, mode: e.target.value as FrequencyMode } })}
                      >
                        {FREQUENCY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </label>
                    {draft.frequency.mode === 'every_n_days' && (
                      <label className="pp-field narrow">
                        <span>Days</span>
                        <input
                          type="number"
                          min={1}
                          max={365}
                          value={draft.frequency.days ?? ''}
                          onChange={(e) => update({ frequency: { ...draft.frequency, days: e.target.value ? Number(e.target.value) : undefined } })}
                        />
                      </label>
                    )}
                    {draft.frequency.mode !== 'once' && (
                      <label className="pp-field narrow">
                        <span>Max times</span>
                        <input
                          type="number"
                          min={1}
                          max={100}
                          placeholder="No cap"
                          value={draft.frequency.maxImpressions ?? ''}
                          onChange={(e) => update({ frequency: { ...draft.frequency, maxImpressions: e.target.value ? Number(e.target.value) : undefined } })}
                        />
                      </label>
                    )}
                  </div>
                  <small className="pp-hint">Counted per account for signed-in users (across their devices), and per install for guests. Within the schedule, "Once" means once for this campaign.</small>
                  {draft.popupId && (
                    <label className="pp-check">
                      <input type="checkbox" checked={draft.showAgain} onChange={(e) => update({ showAgain: e.target.checked })} />
                      Show again to people who already saw it
                    </label>
                  )}
                  {draft.popupId && draft.showAgain && (
                    <small className="pp-hint">This starts a new revision: its Performance numbers start again from zero.</small>
                  )}
                </section>

                <section className="pp-section">
                  <h3>Advanced</h3>
                  <label className="pp-field">
                    <span>Replaces a built-in intro</span>
                    <select value={draft.legacySeenKey} onChange={(e) => update({ legacySeenKey: e.target.value })}>
                      <option value="">No</option>
                      {(catalog?.legacySeenKeys || []).map((key) => (
                        <option key={key} value={key}>{key === 'app_intro' ? 'App intro' : key === 'login_intro' ? 'Login intro' : key}</option>
                      ))}
                    </select>
                  </label>
                  <small className="pp-hint">Devices that already saw the built-in intro count this popup as seen.</small>
                </section>
              </div>

              <aside className="pp-preview">
                <div className="pp-preview-head">
                  <span>Preview</span>
                  <select value={language} onChange={(e) => setLanguage(e.target.value)}>
                    {LANGUAGES.map(({ code, name }) => <option key={code} value={code}>{name}</option>)}
                  </select>
                </div>
                <PopupPreview draft={draft} language={language} />
                <small className="pp-hint">Approximate — check on a device before enabling.</small>
              </aside>
            </div>

            <div className="pp-modal-foot">
              {formError && <div className="pp-form-error">{formError}</div>}
              <button className="pp-secondary-btn" onClick={closeEditor} disabled={saving || uploading}>Cancel</button>
              <button className="pp-primary-btn" onClick={handleSave} disabled={saving || uploading}>
                {saving ? 'Saving…' : draft.popupId ? 'Save' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
